// src/domains/core/context-intelligence/hooks/useContextInsights.ts

import type { Insight, InsightQuery } from "../types/insights.types";

import { useState, useEffect, useCallback, useMemo } from "react";

import { insightService } from "../services/insightService";

/**
 * Hook for accessing context insights
 * Main hook for frontend components
 *
 * @param query - Optional query parameters
 *
 * @returns Object with insights, loading state, and helper functions
 *
 */
export function useContextInsights(query?: InsightQuery) {
  const [insights, setInsights] = useState<Insight[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);

  // Stabilize query object to prevent infinite loops
  const stableQuery = useMemo(() => query, [query]);

  const fetchInsights = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const data = await insightService.getActiveInsights(stableQuery);
      setInsights(data);
    } catch (error_) {
      setError(error_ instanceof Error ? error_ : new Error("Failed to fetch insights"));
    } finally {
      setLoading(false);
    }
  }, [stableQuery]);

  useEffect(() => {
    void fetchInsights();
  }, [fetchInsights]);

  const refresh = useCallback((): void => {
    void fetchInsights();
  }, [fetchInsights]);

  // Group insights by priority
  const insightsByPriority = useMemo(() => {
    const grouped: Record<string, Insight[]> = {
      background: [],
      critical: [],
      high: [],
      low: [],
      medium: [],
    };

    for (const insight of insights) {
      const priorityGroup = grouped[insight.priority];
      if (priorityGroup !== undefined) {
        priorityGroup.push(insight);
      }
    }

    return grouped;
  }, [insights]);

  // Get top insights (highest priority)
  const topInsights = useMemo((): Insight[] => {
    const priorityOrder: Record<string, number> = {
      background: 1,
      critical: 5,
      high: 4,
      low: 2,
      medium: 3,
    };
    const sorted = insights.toSorted((a: Insight, b: Insight): number => {
      // Sort by priority first, then by relevance
      const aPriority = priorityOrder[a.priority];
      const bPriority = priorityOrder[b.priority];
      if (aPriority === undefined || bPriority === undefined) {
        return 0;
      }
      const priorityDiff = bPriority - aPriority;
      if (priorityDiff !== 0) {
        return priorityDiff;
      }
      return b.relevance - a.relevance;
    });
    return sorted.slice(0, query?.limit ?? 10);
  }, [insights, query?.limit]);

  return {
    error,
    insights,
    insightsByPriority,
    loading,
    refresh,
    topInsights,
  };
}

/**
 * Hook for getting insights by category
 *
 * @param category - Insight category
 *
 * @returns Object with insights filtered by category
 *
 */
export function useInsightsByCategory(
  category: Insight["category"],
): ReturnType<typeof useContextInsights> {
  return useContextInsights({ category });
}

/**
 * Hook for getting critical/high priority insights only
 *
 * @returns Object with priority insights
 *
 */
export function usePriorityInsights() {
  return useContextInsights({
    limit: 5,
    priority: ["critical", "high"],
  });
}
